import { Component, OnInit } from '@angular/core';
import * as moment from 'moment';
import { Subscription } from 'rxjs';

import { Transaction, Payment, Invoice } from '@custom-types/payment';
import { GlobalDataService } from '@providers/global-data/global-data.service';
import { GlobalFunctionService } from '@providers/global-function/global-function.service';
import { RequestService } from '@providers/request/request.service';
import { RequestV2Service } from '@providers/request-service-v2/request-v2.service';

@Component({
  selector: 'app-balance',
  templateUrl: './balance.component.html',
  styleUrls: ['./balance.component.less']
})
export class BalanceComponent implements OnInit {
  project: any;
  transactions: any[] = [];
  payments: any[] = [];
  invoices: any[] = [];
  invoicesNeedToPay: any = [];
  paymentsNotInvoiced: any[] = [];
  projectObserver: Subscription;
  totalTransactionAmount: number = 0;
  totalPaymentAmount: number = 0;
  totalInvoiceHistory: number = 0;
  paymentProgress: number = 0;
  status = 'loading';

  constructor(
    private globalDataService: GlobalDataService,
    private requestService: RequestService,
    private globalFunction: GlobalFunctionService,
    private requestV2Service: RequestV2Service
  ) {}

  ngOnInit() {
    this.status = 'loading';

    this.projectObserver = this.globalDataService
      .getCurrentProject()
      .subscribe(async(project: any) => {
        if (!project || this.project) {
          return;
        }

        this.project = project;

        const paymentResults = await this.requestV2Service.getProjectPayments(project.id);

        this.payments = paymentResults.items;

        this.calculateTotalPayment();

        this.getTransactions(project.id);
      });
  }

  ngOnDestroy() {
    this.projectObserver.unsubscribe();
  }

  async getTransactions(projectId) {
    try {
      const transactionResults = await this.requestV2Service.getTransactions(projectId, true);
      this.transactions = transactionResults.items;
      this.calculateTotalTransaction();
      this.calculatePaymentCompletion();

      this.getInvoices(projectId);
    } catch (e) {
      this.status = 'idle';
    }
  }

  async getInvoices(projectId) {
    const invoicesResults = await this.requestV2Service.getInvoices(projectId, true);

    this.invoices = invoicesResults.items;

    this.invoices.forEach((invoice) => {
      invoice.totalPrice = invoice._related.invoice_lines.reduce(
        (acc, invoiceLine) => {
          const price = invoiceLine.unit_price * invoiceLine.unit_quantity;
          const vat = Object.keys(invoiceLine.vat).reduce(
            (acc2, item) => {
              return acc2 + invoiceLine.vat[item];
            },
            0
          );

          return acc + price + vat;
        },
        0
      );

      this.totalInvoiceHistory += invoice.totalPrice;
    });

    this.calculateInvoicesCompletion();
    this.fillPayments();
    this.fillNeedToPay();
  }

  private fillPayments(): void {
    this.payments.forEach((payment) => {
      const existPayment = this.invoices.find(i => i.id_payment === payment.id);
      if (typeof existPayment === 'undefined') {
        this.paymentsNotInvoiced.push(payment);
      }
    });
  }

  private fillNeedToPay(): void {
    this.invoices.forEach((invoice: any) => {
      const alreadyPaidAmount = this.transactions.reduce(
        (acc, transaction: any) => {
          return acc + transaction._related.transaction_allocations.reduce(
            (acc2, bd) => {
              if (bd.id_invoice === invoice.id) {
                return acc2 + bd.amount;
              }

              return acc2;
            },
            0
          );
        },
        0
      );

      const totalInvoice = invoice._related.invoice_lines.reduce(
        (acc, line: any) => {
          const price = line.unit_price * line.unit_quantity;
          const vat = Object.keys(line.vat).reduce(
            (acc2, item) => {
              return acc2 + line.vat[item];
            },
            0
          );

          return acc + price + vat;
        },
        0
      );

      if (totalInvoice - alreadyPaidAmount > 0) {
        if (moment().isBefore(moment(invoice.date_limit))) {
          this.invoicesNeedToPay.push({
            date: invoice.date_limit,
            amount: totalInvoice - alreadyPaidAmount,
            status: 'in-time'
          });
        } else {
          this.invoicesNeedToPay.push({
            date: invoice.date_limit,
            amount: totalInvoice - alreadyPaidAmount,
            status: 'late'
          });
        }
      }
    });

    this.totalPaymentAmount = this.invoicesNeedToPay.reduce((acc, item) => acc + item.amount, 0);
  }

  calculateInvoicesCompletion() {
    this.transactions.forEach((transaction: any) => {
      transaction._related.transaction_allocations.forEach((breakdown: any) => {
        const invoice: Invoice | null = this.getInvoiceWithId(breakdown.id_invoice);
        if (invoice == null) {
          return;
        }

        if (invoice.paid === undefined) {
          invoice.paid = 0;
        }

        invoice.paid += breakdown.amount;
      });
    });

    const paymentsPaidIds = [];

    this.invoices.forEach((invoice: any) => {
      invoice.paidPercent = Math.round((invoice.paid / invoice.totalPrice) * 100);

      // Filter paid payments
      if (invoice.paidPercent >= 100) {
        paymentsPaidIds.push(invoice.id_payment);
      }
    });

    this.payments = this.payments.filter((payment: any) => {
      return !paymentsPaidIds.includes(payment.id);
    });

    const progress = Math.round((this.totalTransactionAmount / this.totalInvoiceHistory) * 100);

    this.paymentProgress = progress >= 100 ? 100 : progress;

    this.status = 'idle';
  }

  getInvoiceWithId(invoiceId: string): Invoice | null {
    for (const invoice of this.invoices) {
      if (invoice.id === invoiceId) {
        return invoice;
      }
    }
    return null;
  }

  calculatePaymentCompletion() {
    if (
      !this.transactions ||
      this.transactions.length <= 0 ||
      !this.payments ||
      this.payments.length <= 0
    ) {
      return;
    }

    let totalTransaction = this.totalTransactionAmount;
    if (totalTransaction === 0) {
      return;
    }

    this.payments.forEach((payment: Payment) => {
      if (totalTransaction <= 0) {
        payment.completion = 0;
        return;
      }

      if (totalTransaction >= payment.amount) {
        payment.completion = 100;
      } else {
        payment.completion = Math.round((totalTransaction / payment.amount) * 100.0);
      }

      totalTransaction -= payment.amount;
    });
  }

  private calculateTotalTransaction(): void {
    let total = 0;

    this.transactions.forEach((transaction: Transaction) => {
      if (transaction.type === 'DEBIT') {
        total -= transaction.amount;
      } else {
        total += transaction.amount;
      }
    });

    this.totalTransactionAmount = total;
  }

  private calculateTotalPayment(): void {
    let total = 0;

    this.payments.forEach((payment: Payment) => {
      total += payment.amount;
    });

    this.totalPaymentAmount = total;
  }
}
